/**
 * DI-01 — does this prompt ask to summarise the deck? Spec phrasings, plus "in N bullets" (default 6).
 * It must name the deck (or be a bare TL;DR), so "summarise this slide" falls through.
 */

import { DEFAULT_SUMMARY_BULLETS, MAX_SUMMARY_BULLETS } from "../../../const";
import type { SummariseDeckIntent } from "../types";

const LEAD = String.raw`^\s*(?:please\s+)?(?:can\s+you\s+|could\s+you\s+)?`;
const DECK = String.raw`(?:deck|presentation|slides|slide\s*deck)`;
const THIS = String.raw`(?:this|the|my|whole|entire|full|\s)*`;
// Tolerates the usual misspellings: sumarise, summerise, summarize, sumarize.
const SUMMARISE = "sum{1,2}[ae]ri[sz]e";
const SUMMARY = "sum{1,2}[ae]ry";

const PATTERNS: readonly RegExp[] = [
  // summarise / summarize / sum up [this|the|whole] deck
  new RegExp(String.raw`${LEAD}(?:${SUMMARISE}|sum\s+up)\s+${THIS}${DECK}\b`, "i"),
  // [give me a] summary of [this|the] deck
  new RegExp(String.raw`${LEAD}(?:give\s+me\s+)?(?:a\s+)?${SUMMARY}\s+of\s+${THIS}${DECK}\b`, "i"),
  // deck summary / presentation summary
  new RegExp(String.raw`${LEAD}(?:a\s+)?${DECK}\s+${SUMMARY}\b`, "i"),
  // what's / what is in this deck
  new RegExp(String.raw`${LEAD}what(?:'s|’s|\s+is)\s+in\s+${THIS}${DECK}\b`, "i"),
  // TL;DR / tldr on its own, optionally "of this deck"
  /^\s*tl;?\s?dr\b/i,
];

const UNIT = String.raw`(?:bullets?|bullet\s+points?|points?|sentences?|lines?)`;
const COUNT = new RegExp(String.raw`\bin\s+(\d{1,3})\s+${UNIT}\b`, "i");
// After `/summarise` the count can stand alone: "4", "4 bullets", "in 4 points".
const SLASH_COUNT = new RegExp(String.raw`^(?:in\s+)?(\d{1,3})(?:\s+${UNIT})?\b`, "i");

/** The matched count clamped to 1…MAX_SUMMARY_BULLETS, or the default when there is none. */
function clampedCount(match: RegExpExecArray | null): number {
  if (!match?.[1]) return DEFAULT_SUMMARY_BULLETS;
  const n = Number.parseInt(match[1], 10);
  return Math.min(Math.max(n, 1), MAX_SUMMARY_BULLETS);
}

/** Reads "in N bullets". */
function bulletCountOf(text: string): number {
  return clampedCount(COUNT.exec(text));
}

/** `/summarise [N bullets]`: always a deck summary; the words after the slash only set the count. */
export function summariseSlashIntent(args: string): SummariseDeckIntent {
  const text = args.trim();
  return { bulletCount: clampedCount(SLASH_COUNT.exec(text) ?? COUNT.exec(text)) };
}

export function matchSummariseDeckIntent(prompt: string): SummariseDeckIntent | null {
  const text = prompt.trim();
  if (!text || text.startsWith("/")) return null;
  if (!PATTERNS.some((re) => re.test(text))) return null;
  return { bulletCount: bulletCountOf(text) };
}
